import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Home, Wrench, Calendar, Image, Mail, Shield, TrendingUp, Users, DollarSign } from "lucide-react";
import { useAuth } from "../App";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const publicNavLinks = [
    { path: "/", label: "Home", icon: Home },
    { path: "/services", label: "Services", icon: Wrench },
    { path: "/financing", label: "Financing", icon: DollarSign },
    { path: "/booking", label: "Book Now", icon: Calendar },
    { path: "/gallery", label: "Gallery", icon: Image },
    { path: "/contact", label: "Contact", icon: Mail },
  ];

  const adminNavLinks = [
    { path: "/admin", label: "Admin", icon: Shield },
    { path: "/crm", label: "Sales", icon: TrendingUp },
  ];

  // Show client portal if logged in as client, admin links if admin, otherwise show login links
  let navLinks;
  if (user?.role === "client") {
    navLinks = [...publicNavLinks, { path: "/client-portal", label: "My Portal", icon: Users }];
  } else if (user?.role === "admin") {
    navLinks = [...publicNavLinks, ...adminNavLinks];
  } else {
    navLinks = [...publicNavLinks, { path: "/client-login", label: "Client Login", icon: Users }];
  }

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-gradient-to-br from-[#4A2B6E] via-[#0D5C63] to-[#FF6B35] p-0.5">
              <div className="w-full h-full rounded-full bg-[#0B0C10] flex items-center justify-center">
                <span className="text-lg md:text-xl font-bold gradient-text">NL</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <p className="text-lg md:text-xl font-bold text-white tracking-tight">New Leaf PMC</p>
              <p className="text-xs text-[#A0AAB2]">Handyman & Renovation</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? "bg-[#FF6B35] text-white"
                    : "text-[#A0AAB2] hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <a
            href="tel:863-340-9244"
            className="hidden md:flex items-center space-x-2 gradient-btn px-5 py-2.5 rounded-lg font-semibold text-sm"
            data-testid="nav-call-btn"
          >
            <Phone size={16} />
            <span>863-340-9244</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/10"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 glass border-t border-[#2A2B32]" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive(link.path)
                      ? "bg-[#FF6B35] text-white"
                      : "text-[#A0AAB2] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
            <a
              href="tel:863-340-9244"
              className="flex items-center justify-center space-x-2 gradient-btn px-4 py-3 rounded-lg font-semibold mt-4"
              data-testid="mobile-call-btn"
            >
              <Phone size={18} />
              <span>Call Now: 863-340-9244</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
